import { useEffect, useRef, useState } from "react";
import { ai, aiChanged, type AskReply, type ChatTurn, type Proposal } from "../../api/ai";
import { accounting, customers } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { Check, Sparkles, X } from "../icons";
import { Pill } from "../parts";
import type { View } from "../routes";
import { errorText, inr } from "../purchases/shared";
import { useAiStatus } from "./useAiStatus";
import "./ai.css";
import { t } from "../../i18n";

/*
 * The assistant (server/ai-assistant.js): the "Ask AI" button in the top bar opens a chat panel. Answers come from the reports
 * and lists the signed-in person may open; the server checks every tool call against their role. The assistant can only draft:
 * each draft (a reminder email, a customer note, a bank line's category) is shown here with a button that calls the ordinary
 * route, and only when the person presses it. Everything from the AI is shown as plain text. The chat lives only in this tab:
 * nothing of it is kept on the server or the device.
 */

type Entry = { role: "user"; text: string } | { role: "assistant"; reply: AskReply } | { role: "error"; text: string };

const STARTERS = ["Who owes us the most?", "MRR this quarter vs last", "GST payable for last month", "Suggest categories for uncategorised bank lines"];

export default function AiAssistant({ go, notify }: { go: (view: View, id?: string | null) => void; notify: (m: string) => void }) {
  const status = useAiStatus();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" }); // newer browsers return a promise here: never hand it to React as a clean-up
  }, [entries, busy]);
  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!status?.available || !status.assistant) return null;

  async function ask(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    // The last few turns go back with the question (as text); the server keeps none of it.
    const history: ChatTurn[] = entries
      .filter((e): e is Exclude<Entry, { role: "error" }> => e.role !== "error")
      .slice(-6)
      .map((e) => (e.role === "user" ? { role: "user", text: e.text } : { role: "assistant", text: e.reply.answer }));
    setEntries((x) => [...x, { role: "user", text: q }]);
    setDraft("");
    setBusy(true);
    try {
      const reply = await ai.ask(q, history);
      setEntries((x) => [...x, { role: "assistant", reply }]);
    } catch (e) {
      setEntries((x) => [...x, { role: "error", text: errorText(e, t("The assistant could not answer.")) }]);
      aiChanged(); // the cap may have been reached
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button type="button" className="ai-launch" aria-label={t("Ask AI")} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <Sparkles size={15} />
        <span>{t("Ask AI")}</span>
      </button>
      {open && (
        <aside className="ai-panel" role="dialog" aria-label={t("Assistant")}>
          <header className="ai-panel__head">
            <div>
              <strong>{t("Assistant")}</strong> {status.mock && <Pill tone="amber">Test mode</Pill>}
              <small>{t("Answers from reports your role can open. It drafts; you decide. Nothing is sent or saved by the assistant.")}</small>
            </div>
            <button type="button" className="icon-button" aria-label={t("Close the assistant")} onClick={() => setOpen(false)}>
              <X size={18} />
            </button>
          </header>
          <div className="ai-panel__body">
            {entries.length === 0 && (
              <div className="ai-starters">
                <p className="vp-muted">{t("Ask about your books, for example:")}</p>
                {STARTERS.map((s) => (
                  <button key={s} type="button" className="button button--secondary button--sm" onClick={() => void ask(s)}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            {entries.map((e, i) =>
              e.role === "user" ? (
                <p key={i} className="ai-msg is-user">
                  {e.text}
                </p>
              ) : e.role === "error" ? (
                <p key={i} className="ai-msg is-error" role="alert">
                  {e.text}
                </p>
              ) : (
                <Answer key={i} reply={e.reply} go={go} notify={notify} onNavigate={() => setOpen(false)} />
              ),
            )}
            {busy && <p className="ai-msg is-busy">{t("Looking it up…")}</p>}
            <div ref={end} />
          </div>
          {status.cap_reached && <p className="crm-note is-red ai-panel__note">{t("This month's AI allowance is used up. An Admin can raise it in Settings › AI.")}</p>}
          <form
            className="ai-panel__ask"
            onSubmit={(e) => {
              e.preventDefault();
              void ask(draft);
            }}
          >
            <textarea
              ref={input}
              rows={2}
              value={draft}
              maxLength={1000}
              placeholder={t("Ask about customers, invoices, MRR, GST, bills…")}
              aria-label={t("Your question")}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void ask(draft);
                }
              }}
            />
            <button className="button button--primary" disabled={busy || !draft.trim()}>
              {t("Ask")}
            </button>
          </form>
        </aside>
      )}
    </>
  );
}

function Answer({ reply, go, notify, onNavigate }: { reply: AskReply; go: (view: View, id?: string | null) => void; notify: (m: string) => void; onNavigate: () => void }) {
  return (
    <div className="ai-msg is-assistant">
      <p className="ai-answer">{reply.answer}</p>
      {reply.tool_calls.length > 0 && (
        <p className="ai-tools">
          {reply.tool_calls.map((c, i) => (
            <Pill key={i} tone={c.status === "done" ? "neutral" : c.status === "denied" ? "amber" : "red"}>
              {`${c.label}${c.status === "denied" ? t(" · not allowed for your role") : c.status === "error" ? t(" · failed") : ""}`}
            </Pill>
          ))}
        </p>
      )}
      {reply.proposals.map((p, i) => (
        <ProposalCard key={i} p={p} go={go} notify={notify} onNavigate={onNavigate} />
      ))}
    </div>
  );
}

/** A draft from the assistant. Nothing happens until the person presses the button, which calls the ordinary route. */
function ProposalCard({ p, go, notify, onNavigate }: { p: Proposal; go: (view: View, id?: string | null) => void; notify: (m: string) => void; onNavigate: () => void }) {
  const { allow } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subject, setSubject] = useState(p.type === "reminder_email" ? p.subject : "");
  const [text, setText] = useState(p.type === "reminder_email" ? p.body : p.type === "customer_note" ? p.note : "");
  const run = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setDone(true);
      notify(message);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (p.type === "reminder_email") {
    return (
      <div className="ai-draft-card">
        <p className="ai-draft-card__title">
          {t("Draft reminder · {v} · {v2} {v3}", { v: p.invoice_number ?? t("invoice"), v2: p.customer_name ?? "", v3: p.balance ? t("· {amount} due", { amount: inr(p.balance) }) : "" })}
        </p>
        <small>{t("To: {v} (not sent: copy it into your email, or use the invoice's own Send)", { v: p.email ?? t("the customer's email") })}</small>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} aria-label={t("Subject")} />
        <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} aria-label={t("Email text")} />
        <p className="pur-actions">
          <button type="button" className="button button--secondary button--sm" onClick={() => void navigator.clipboard?.writeText(`${subject}\n\n${text}`).then(() => notify(t("Copied the draft.")), () => notify(t("Could not copy; select the text instead.")))}>
            {t("Copy")}
          </button>
          <button
            type="button"
            className="button button--secondary button--sm"
            onClick={() => {
              onNavigate();
              go("salesinvoices", p.invoice_id);
            }}
          >
            {t("Open the invoice")}
          </button>
        </p>
      </div>
    );
  }
  if (p.type === "customer_note") {
    const can = allow("customers", "edit");
    return (
      <div className="ai-draft-card">
        <p className="ai-draft-card__title">{t("Draft note for {v}", { v: p.customer_name ?? t("the customer") })}</p>
        <textarea rows={3} value={text} disabled={done} onChange={(e) => setText(e.target.value)} aria-label={t("Note")} />
        {error && <small className="vp-bad">{error}</small>}
        <p className="pur-actions">
          {done ? (
            <Pill tone="green">Added to the customer's notes</Pill>
          ) : (
            <button
              type="button"
              className="button button--primary button--sm"
              disabled={!can || busy || !text.trim()}
              title={can ? undefined : t("Your role cannot edit customers.")}
              onClick={() =>
                void run(async () => {
                  const c = await customers.get(p.customer_id);
                  await customers.update(p.customer_id, { notes: [c.notes, text.trim()].filter(Boolean).join("\n\n") });
                }, t("Note added to the customer."))
              }
            >
              {t("Add to the customer's notes")}
            </button>
          )}
        </p>
      </div>
    );
  }
  const can = allow("accounting", "edit");
  return (
    <div className="ai-draft-card">
      <p className="ai-draft-card__title">
        {p.date} · {p.description ?? t("Bank line")} · <span className="num">{inr(p.amount)}</span> {p.kind === "deposit" ? "in" : t("out")}
      </p>
      <small>
        {t("Suggested:")} <strong>{p.account_name}</strong> ({p.reason})
      </small>
      {error && <small className="vp-bad">{error}</small>}
      <p className="pur-actions">
        {done ? (
          <Pill tone="green">
            <Check size={12} /> {t("Categorised")}
          </Pill>
        ) : (
          <button type="button" className="button button--primary button--sm" disabled={!can || busy} title={can ? undefined : t("Your role cannot categorise bank lines.")} onClick={() => void run(() => accounting.categorize(p.transaction_id, p.account_id), t("Categorised as {account_name}.", { account_name: p.account_name }))}>
            {t("Accept")}
          </button>
        )}
      </p>
    </div>
  );
}
